import type { Rechenweg, RechenwegQuelle } from '@symphenia/calc-core'
import { api } from '@/lib/axios'

/**
 * AP-37 Detailblatt N (N-V) — Läufe, Vorschlagsliste und Befunde
 * (`/calculations/:id/ai-runs`, `/ai-findings`, Baustein `CALCULATION.AI`).
 */
export type KiSicherheit = 'HIGH' | 'MEDIUM' | 'LOW'
export type KiZeilenArt = 'PRICE' | 'COST_LINE' | 'WORK_STEP' | 'POSITION_WITH_SHEET' | 'PATCH' | 'GAP_ANSWER' | 'CHECK_NOTE' | 'GAP_MISSING'

export interface KiLauf {
  id: string
  capability: string
  executor: 'SYMPHENIA' | 'EXTERNAL'
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'PARTIAL' | 'FAILED' | 'REJECTED'
  model: string | null
  report: {
    accepted?: number
    findings?: number
    dropped?: { ref: string; grund: string; text: string; detail?: string }[]
    droppedByReason?: Record<string, number>
    notes?: string[]
    errors?: string[]
  } | null
  error: string | null
  createdAt: string
  finishedAt: string | null
  openLines?: number
}

export interface KiZeile {
  id: string
  ref: string
  kind: KiZeilenArt
  targetPositionId: string | null
  parentLineId: string | null
  payload: Record<string, unknown>
  derivation: Rechenweg | null
  confidence: KiSicherheit
  preselected: boolean
  positionChanged: boolean
  findingIds: string[]
  notes: string[]
  status: 'OPEN' | 'ACCEPTED' | 'REJECTED' | 'DROPPED'
  executor: 'SYMPHENIA' | 'EXTERNAL'
  modelClaimed: string | null
  edited: boolean
  position: { label: string; shortText: string; quantity: number; unitCode: string | null; deleted: boolean } | null
}

export interface KiBefund {
  id: string
  topic: string
  text: string
  value: number | null
  unit: string | null
  source: RechenwegQuelle
  confidence: KiSicherheit
  status: 'OPEN' | 'CONFIRMED' | 'CORRECTED' | 'REJECTED' | 'STALE'
  correctedValue: number | null
  correctedText: string | null
  positionRefs: string[]
}

const basis = (id: string) => `/calculations/${id}`

export const fetchKiLaeufe = (id: string) =>
  api.get<{ data: KiLauf[]; meta: { openTotal: number } }>(`${basis(id)}/ai-runs`).then((r) => r.data)
export const fetchKiLauf = (id: string, rid: string) =>
  api.get<{ data: KiLauf & { lines: KiZeile[]; findings: KiBefund[] } }>(`${basis(id)}/ai-runs/${rid}`).then((r) => r.data.data)
export const kiUebernehmen = (id: string, rid: string, lineIds: string[], edits: { lineId: string; value: number }[]) =>
  api.post<{ data: { accepted: number; dropped: number } }>(`${basis(id)}/ai-runs/${rid}/apply`, { lineIds, edits }).then((r) => r.data.data)
export const kiVerwerfen = (id: string, rid: string, lineIds: string[]) => api.post(`${basis(id)}/ai-runs/${rid}/reject`, { lineIds })
export const fetchKiBefunde = (id: string) => api.get<{ data: KiBefund[] }>(`${basis(id)}/ai-findings`).then((r) => r.data.data)
export const kiBefundEntscheiden = (id: string, fid: string, dto: { status: 'CONFIRMED' | 'CORRECTED' | 'REJECTED'; correctedValue?: number | null; correctedText?: string | null }) =>
  api.patch<{ data: KiBefund }>(`${basis(id)}/ai-findings/${fid}`, dto).then((r) => r.data.data)

// N1 — eingebaute Läufe (V1 Preise aus Angeboten)
export interface KiSchaetzung {
  files: { id: string; name: string; pages: number | null; text: boolean }[]
  inputTokens: number
  outputTokens: number
  model: string | null
  provider: string
  keySource: 'TENANT' | 'CENTRAL'
  consentCurrent: boolean
  capabilityOn: boolean
  quota: { limit: number; used: number; warn: boolean; exhausted: boolean }
  costLines: number
  /** V6: offene Bieterlücken */
  gaps: number
  /** CHECK: Positionen der Kalkulation */
  positions?: number
}
/** N1, N6: die eingebauten Fähigkeiten mit Lauf-Dialog */
export type EingebauteFaehigkeit = 'V1' | 'V2' | 'V6' | 'CHECK'
export interface KiLaufAuftrag {
  capability: EingebauteFaehigkeit
  fileIds?: string[]
  positionIds?: string[]
}
export const schaetzeKiLauf = (id: string, a: KiLaufAuftrag) => api.post<{ data: KiSchaetzung }>(`${basis(id)}/ai-runs/estimate`, a).then((r) => r.data.data)
export const starteKiLauf = (id: string, a: KiLaufAuftrag) => api.post<{ data: KiLauf }>(`${basis(id)}/ai-runs`, a).then((r) => r.data.data)

// N2 — Stückliste aus Excel/CSV ohne KI
export type ListenFeld = 'bezeichnung' | 'menge' | 'einheit' | 'laenge' | 'gewicht' | 'werkstoff' | 'position'
export type Spaltenzuordnung = Partial<Record<ListenFeld, string>> & { laengeEinheit?: 'mm' | 'm' }
export interface MaterialListeVorschau {
  headers: string[]
  rows: string[][]
  rowCount: number
  mapping: Spaltenzuordnung
  remembered: boolean
}
export const materialListeVorschau = (id: string, fileId: string) =>
  api.post<{ data: MaterialListeVorschau }>(`${basis(id)}/material-list/preview`, { fileId }).then((r) => r.data.data)
export const liesMaterialListe = (id: string, dto: { fileId: string; mapping: Spaltenzuordnung; positionId?: string | null; remember?: boolean }) =>
  api.post<{ data: KiLauf }>(`${basis(id)}/material-list/read`, dto).then((r) => r.data.data)
export interface ListenZuordnung {
  id: string
  signature: string
  columns: Spaltenzuordnung
  updatedAt: string
}
export const fetchListenZuordnungen = () => api.get<{ data: ListenZuordnung[] }>('/calculation-settings/ai/list-mappings').then((r) => r.data.data)
export const loescheListenZuordnung = (mid: string) => api.delete(`/calculation-settings/ai/list-mappings/${mid}`)
